// -----------------------------------------------------------------------------
// V2BookingDetailsStep — #64a / #90 (V2-native Book wizard, step 1 of 5)
//
// 2026-06-07 #90 redesign: replaced V1-styled inner chrome (uppercase section
// headers, gridStyle, RHFToggleGroup, rounded-10px inputs) with V2 primitives
// matching the Attended-family family of popups. Behavior unchanged: same
// Redux dispatches, same Yup schema, same form field names, same imperative
// submit API, same endpoints.
//
// V2 primitives mirrored:
//   • Sentence-case section labels — LogCallPanel.jsx:126,148,165 (12px / 600 / #3c3c43 / letterSpacing -0.005em)
//   • Sub-section "Quick pick" caption — RescheduleDialog.jsx:140-146 (11px / 600 / uppercase / #6e6e73)
//   • Preset chip styling — RescheduleDialog.jsx:159-178 (bgcolor rgba(0,0,0,0.04), hover rgba(7,141,238,0.10))
//   • Outlined pill CTA (Save Treatment / suggestions) — FollowUpV2.jsx:1950-1974 (brand green #00AB55, borderRadius 999)
//   • Section dividers — PANEL_HEADER (borderBottom rgba(0,0,0,0.06))
//   • Avatar+chip grid is already V2-native (was rebuilt in #75) — kept verbatim.
//
// All endpoints/dispatches/schema/imperative API identical to V1 (no BE changes).
// "No" radio branch (V1:727-829) is dead in followUp — radioButton hardcoded 'Yes'
// per useTreatmentAndAppointment.getType() (hooks/useTreatmentAndAppointment.js:88-95).
// -----------------------------------------------------------------------------
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { Box, Typography, Button, ButtonBase, Tooltip } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { Iconify } from 'src/components';
import { FormProvider } from 'src/components/hook-form';
import TreatmentEditPopover from 'src/pages/dashboard/tables/AllLeadTable/TreatmentEditPopover';
import TreatmentFlowComponent from 'src/components/treatmentComponents/TreatmentFlowComponent';
// Shared with the Dentally mode's sections so both modes use one panel treatment.
import { bookingStepPanelStyle } from 'src/styles/DentallyBookingStyle';
import { useSelector, dispatch } from 'src/redux/store';
import {
    addAppointment,
    UpdateAppointment,
    updateSteps,
    saveNewLeadDetail,
    savePersonalLeadDetails,
    getBookingSuggestions,
} from 'src/redux/slices/lead';
import { useAddTreatment, useTreatmentAndAppointment } from 'src/hooks';
import usePracticeStaff from 'src/hooks/usePracticeStaff';
import { TreatmentAndAppointmentSchema } from 'src/validations/validations';
import { localStorageGetItem, localStorageSetItem } from 'src/utils/storage-available';
// Phase 2 stale-tab RCA — preserve in-flight form state across reload
import useFormStatePreservation from 'src/hooks/useFormStatePreservation';
import { isBookingDetailsStepValid } from './bookingDetailsValidity';
import { hasPractitionerRole } from '../../../../utils/practiceUserRoles';

// ── V2 chrome constants ──────────────────────────────────────────────────────
// Step content is rendered INSIDE an ActionPanel that already supplies the
// outer card chrome (rounded white panel + 1px border + soft shadow) AND a
// header (icon + "Book appointment" title). Adding our own PANEL_CHROME +
// PANEL_HEADER stacked a second bordered/shadowed "Booking details" box
// inside that — the doubled-card complaint Raj raised on 2026-06-08.
//
// We now render the step as plain content: no extra background, no extra
// border, no extra header. The section labels and dividers below carry the
// hierarchy.

// Section header — V2 sentence-case (LogCallPanel.jsx:126).
const SECTION_LABEL = {
    fontSize: '13px', fontWeight: 600, color: '#1d1d1f',
    letterSpacing: '-0.005em', mb: 0.75,
};
// Sub-label (e.g. "Booked with", "Assigned to") — V2 sentence-case (LogCallPanel.jsx:148).
const SUB_LABEL_V2 = {
    fontSize: '12px', fontWeight: 600, color: '#3c3c43',
    letterSpacing: '-0.005em', mb: 0.5,
};
// Section row spacing. The divider that used to separate sections is gone
// (2026-08-17): each section's content now sits on its own grey panel, matching the
// Dentally mode's Treatment / Team / Availability sections, so a rule between them
// would be a second, redundant grouping cue.
const SECTION_BLOCK = { mb: 1.5 };
const SECTION_BLOCK_LAST = { pb: 0.25 };

// V2 chip-group choice — mirrors FollowUpV2.jsx:1949-1974 outlined pill +
// RescheduleDialog.jsx:159-178 preset chip. Brand-green selected state.
const choiceChipSx = (isSelected) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    px: 1.5, py: 0.6,
    borderRadius: '999px',
    fontSize: '12px', fontWeight: 600,
    letterSpacing: '-0.005em',
    border: '1px solid',
    transition: 'all 150ms ease',
    cursor: 'pointer',
    textTransform: 'none',
    ...(isSelected
        ? { color: '#00AB55', borderColor: '#00AB55', bgcolor: 'rgba(0,171,85,0.08)' }
        : { color: '#3c3c43', borderColor: 'rgba(0,0,0,0.12)', bgcolor: 'transparent',
            '&:hover': { borderColor: '#00AB55', bgcolor: 'rgba(0,171,85,0.06)' } }),
});

// Quick-pick chip — mirrors RescheduleDialog.jsx:163-174 verbatim.
const quickPickChipSx = {
    textTransform: 'none', fontSize: '12px', fontWeight: 500,
    borderRadius: '8px', py: 0.4, px: 1.25, minWidth: 0,
    color: '#3c3c43', bgcolor: 'rgba(0,0,0,0.04)',
    '&:hover': { bgcolor: 'rgba(7,141,238,0.10)', color: '#078DEE' },
};

// V2 outlined pill button — mirrors FollowUpV2.jsx:1950-1974 verbatim.
const v2PillBtnSx = (isSelected = false) => ({
    color: isSelected ? '#fff' : '#00AB55',
    borderColor: isSelected ? '#00AB55' : 'rgba(0,171,85,0.48)',
    bgcolor: isSelected ? '#00AB55' : 'transparent',
    borderRadius: '999px',
    fontWeight: 600, fontSize: '13px',
    letterSpacing: '-0.005em', textTransform: 'none',
    px: 2, py: 0.6, minHeight: 32,
    boxShadow: 'none',
    '&:hover': {
        borderColor: '#00AB55',
        bgcolor: isSelected ? '#0a8f47' : 'rgba(0,171,85,0.08)',
        boxShadow: 'none',
    },
    '&:active': { bgcolor: isSelected ? '#0a8f47' : 'rgba(0,171,85,0.16)' },
});

// V2 input chrome — TreatmentFlowComponent wrapper, mirrors RescheduleDialog input radii.
const V2_INPUT_WRAP_SX = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        fontSize: '13px',
        '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
        '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.24)' },
        '&.Mui-focused fieldset': { borderColor: '#00AB55' },
    },
    '& .MuiInputBase-input': { fontSize: '13px', letterSpacing: '-0.005em' },
    '& .MuiInputLabel-root': { fontSize: '13px', letterSpacing: '-0.005em' },
};

// Local V2 chip-group — drop-in for RHFToggleGroup, no V1 styling.
function V2ChoiceGroup({ label, value, onChange, options }) {
    return (
        <Box>
            <Typography sx={SUB_LABEL_V2}>{label}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {options.map((opt) => {
                    const isSelected = value === opt.value;
                    return (
                        <ButtonBase
                            key={opt.value}
                            onClick={() => onChange(opt.value)}
                            sx={choiceChipSx(isSelected)}
                            aria-pressed={isSelected}
                        >
                            {opt.label}
                        </ButtonBase>
                    );
                })}
            </Box>
        </Box>
    );
}
V2ChoiceGroup.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    })).isRequired,
};
V2ChoiceGroup.defaultProps = { value: '' };

const V2BookingDetailsStep = forwardRef(({ handleNext, onValidityChange }, ref) => {
    // ── Redux mirrors V1:37-41 ────────────────────────────────────────────────
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { treatmentsListV2 } = useSelector((state) => state.practiceTreatment);
    const { newLeadDetail, tableRowLead } = useSelector((state) => state.lead);
    const { practiceUser } = useSelector((state) => state.practiceUser);
    const { appointmentType, appointmentMethod } = useSelector((state) => state.schema);

    // ── Local state mirrors V1:42-56 ──────────────────────────────────────────
    const [treatmentData, setTreatment] = useState('');
    const { enqueueSnackbar } = useSnackbar();
    const { getTreatment, getAssignUser, handleSuccessMessage } =
        useTreatmentAndAppointment({ isOverview: true, type: 'followUp' });
    const [assignToState, setAssignToState] = useState(
        (newLeadDetail?.lead_appointment && getAssignUser()) || []
    );
    const [assignToLeadState, setAssignToLeadState] = useState([]);
    const [anchorEditTreatment, setAnchorEditTreatment] = useState(null);
    const [priceAmountError, setPriceAmountError] = useState(false);
    const [teethAmountError, setTeethAmountError] = useState(false);
    let localTreatmentData = {};
    try { localTreatmentData = JSON.parse(localStorageGetItem('localTreatmentData')) || {}; } catch { /* corrupt localStorage */ }

    const { toggle: EditTreatment, onOpen: onEditTreatment, onClose: onCloseEditTreatment } = useAddTreatment();
    const [practSearch, setPractSearch] = useState('');
    const [showAllPract, setShowAllPract] = useState(false);
    const MAX_VISIBLE_PRACTITIONERS = 7;
    // "Assigned to" had no collapse or search at all and rendered every assignable
    // user — 30+ unlabelled circles on a practice of any size. Same controls as
    // "Booked with" above so the two rows behave identically.
    const [assignSearch, setAssignSearch] = useState('');
    const [showAllAssign, setShowAllAssign] = useState(false);

    const { assignableUsers, bookableStaff } = usePracticeStaff();

    const getDate = (date) => {
        const cleaned = typeof date === 'string' ? date.replace('.0Z', '') : date;
        return moment(cleaned, ['DD-MM-YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm:ss', moment.ISO_8601]);
    };

    const [amount, setAmount] = useState(getTreatment('price') || 0.00);
    const [suggestions, setSuggestions] = useState(null);

    // ── Form defaults mirror V1:75-89 (followUp branch always "Yes") ─────────
    const defaultValues = {
        treatment: getTreatment('treatment'),
        treatmentValue: amount,
        teethAmount: getTreatment('teeth') || null,
        radioButton: 'Yes',
        appoinmentDate: (() => {
            // Guard against Invalid Date. lead_appointment can be present but have
            // no (or a malformed) datetime — e.g. a NEW lead with no real appointment —
            // which made `new Date(getDate(undefined))` an Invalid Date and crashed the
            // MUI date-fns DatePicker ("RangeError: Invalid time value") when it opened.
            const apptDatetime = newLeadDetail?.lead_appointment?.datetime;
            if (!apptDatetime) return null;
            const parsed = getDate(apptDatetime);
            return parsed && parsed.isValid() ? parsed.toDate() : null;
        })(),
        bookedWith: (newLeadDetail?.lead_appointment && getAssignUser()) || [],
        practiceContactedVia:
            (newLeadDetail && newLeadDetail.lead_appointment && newLeadDetail.lead_appointment.contacted_via)
            || 'PHONE',
        preferredContactMethod:
            (newLeadDetail && newLeadDetail.lead_appointment && newLeadDetail.lead_appointment.method)
            || 'IN_PRACTICE',
        appointmentType:
            (newLeadDetail && newLeadDetail.lead_appointment && newLeadDetail.lead_appointment.type)
            || 'CONSULTATION',
        // Reminder fields kept in schema defaults to satisfy validators even
        // though the "No" branch is unreachable in followUp.
        reminderDateTime: '',
        OtherNotes: '',
        reminder: '',
        assigneesNoBook: [],
    };

    useEffect(() => {
        if (defaultValues.treatment) setTreatment(defaultValues.treatment);
    }, [defaultValues.treatment]);

    // Mirror V1:104-108 — booking always runs payment flow.
    useEffect(() => {
        dispatch(updateSteps('payment'));
    }, []);

    // ── Form-state preservation (Phase 2 stale-tab RCA, 2026-06-08) ──────────
    // Keyed by lead id so a forced reload restores this lead's Step 1 input.
    const fspLeadId = newLeadDetail?.id || tableRowLead?.id || 'none';
    const { restore: fspRestore, save: fspSave, clear: fspClear } = useFormStatePreservation(
        'v2-book-step1',
        fspLeadId,
    );
    const persistedValues = fspRestore();

    // Restored form state can carry a raw ISO *string* appoinmentDate (untagged by
    // the preservation reviver). Merged over the guarded default, that string reaches
    // the MUI date-fns DateTimePicker and throws "RangeError: Invalid time value" on
    // open — re-crashing booking for anyone with a stale sessionStorage entry. Coerce
    // the merged appoinmentDate to a valid Date or null before it hits the form.
    const mergedDefaultValues = { ...defaultValues, ...(persistedValues || {}) };
    {
        const v = mergedDefaultValues.appoinmentDate;
        if (!v) {
            mergedDefaultValues.appoinmentDate = null;
        } else if (v instanceof Date) {
            mergedDefaultValues.appoinmentDate = Number.isNaN(v.getTime()) ? null : v;
        } else {
            const d = new Date(v);
            mergedDefaultValues.appoinmentDate = Number.isNaN(d.getTime()) ? null : d;
        }
    }

    const methods = useForm({
        resolver: yupResolver(TreatmentAndAppointmentSchema(assignToState, 'custom reminder')),
        mode: 'onSubmit',
        defaultValues: mergedDefaultValues,
    });

    const {
        control, setValue, watch, getValues,
        formState: { errors }, handleSubmit, trigger,
    } = methods;

    useEffect(() => {
        if (!isEmpty(localTreatmentData)) {
            if (localTreatmentData.treatment) {
                setTreatment(localTreatmentData.treatment);
                setValue('treatment', localTreatmentData.treatment);
                setValue('teethAmount', localTreatmentData.teethAmount);
            }
            if (localTreatmentData.price) setAmount(localTreatmentData.price);
        }
    }, []);

    useEffect(() => {
        if (assignToState?.length > 0) trigger('bookedWith');
    }, [assignToState, trigger]);

    // 2026-06-08 LL-3 fix: when the practice has exactly ONE bookable staff member,
    // pre-select them so "Next" isn't blocked behind a forced click. Re-runs when
    // bookableStaff resolves (it loads async via usePracticeStaff). Guard against
    // clobbering a user-made selection by only firing while assignToState is empty.
    useEffect(() => {
        if (
            Array.isArray(bookableStaff)
            && bookableStaff.length === 1
            && (!assignToState || assignToState.length === 0)
        ) {
            handleSetAssign([bookableStaff[0]]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookableStaff]);

    // V1:144-150 — fetch booking suggestions when treatment selected.
    useEffect(() => {
        if (treatmentData?.id && practiceId) {
            dispatch(getBookingSuggestions({ practiceId, treatmentId: treatmentData.id }))
                .then((data) => data && setSuggestions(data));
        }
    }, [treatmentData?.id, practiceId]);

    const values = watch();

    // Phase 2 stale-tab RCA — persist on every RHF change (debounced inside
    // the hook). Also persists local-state `amount` since it is part of Step 1
    // user input but lives outside RHF.
    useEffect(() => {
        fspSave({ ...values, __amount: amount });
    }, [values, amount, fspSave]);

    // Re-hydrate persisted `__amount` once on mount, mirroring the existing
    // localTreatmentData re-hydration above.
    useEffect(() => {
        if (persistedValues && persistedValues.__amount != null && Number(persistedValues.__amount) > 0) {
            setAmount(Number(persistedValues.__amount));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 2026-06-07 bundle: report formState validity up to the parent (FollowUpV2),
    // mirroring the existing setPmsConfirmStepState pattern (FollowUpV2.jsx:381 →
    // OverView.jsx:407). See full rationale in original file history.
    useEffect(() => {
        if (!onValidityChange) return;
        onValidityChange(isBookingDetailsStepValid({
            treatment: values.treatment,
            appoinmentDate: values.appoinmentDate,
            bookedWith: values.bookedWith,
            assignToState,
        }));
    }, [values.treatment, values.appoinmentDate, values.bookedWith, assignToState, onValidityChange]);

    const practiceUserId = (e) => (
        e
            ? practiceUser?.results
                ?.filter((el) => e?.includes(`${el?.user?.salutation || ''} ${el?.user?.first_name || ''} ${el?.user?.last_name || ''}`))
                ?.map((el) => el?.id)
            : []
    );

    // V1:156-162 — push assignee list into redux as user selects.
    useEffect(() => {
        let finalArray = [];
        if (assignToLeadState.length > 0) finalArray = assignToLeadState?.map((e) => e?.id);
        dispatch(savePersonalLeadDetails({ assignees: finalArray }));
    }, [assignToLeadState]);

    // ── onSubmit mirrors V1:164-234, followUp+Yes branch only ────────────────
    const onSubmit = (data, status) => {
        try {
            const detail = {
                addAppointment: true,
                lead_treatment: {
                    treatment: data.treatment,
                    custom_price: amount,
                    teethAmount: data.teethAmount,
                },
                lead_appointment: {
                    datetime: `${moment(getValues('appoinmentDate')).format('DD-MM-YYYY HH:mm:ss')}.0Z`,
                    method: data.preferredContactMethod,
                    contacted_via: data.practiceContactedVia,
                    assignees: assignToState?.map((el) => el?.id),
                    type: data?.appointmentType,
                },
            };
            dispatch(updateSteps('payment'));
            dispatch(saveNewLeadDetail(detail));
            // Phase 2 stale-tab RCA — Step 1 done, drop the snapshot so a
            // reload mid-Step-2+ doesn't restore stale Step 1 values.
            fspClear();
            handleNext(true, status);
        } catch (err) {
            console.error('[V2BookingDetailsStep] Error while submitting', err);
            enqueueSnackbar('Something went wrong. Please try again.', {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
            });
        }
    };

    // ── handleSuccess + callAppointment mirror V1:236-327 (followUp branch) ──
    const handleSuccess = (data) => {
        if (data && data.appoiment === 'Yes' && data.treatmentId) {
            callAppointment(data.treatmentId);
            return;
        }
        handleSuccessMessage({ data });
        handleNext(true);
    };

    const callAppointment = (treatmentId) => {
        const contactedVia = getValues('practiceContactedVia');
        if (!treatmentId) return;
        if (newLeadDetail && newLeadDetail.lead_appointment) {
            if (
                moment(getDate(newLeadDetail.lead_appointment.datetime)).format('DD-MM-YYYY HH:mm:ss')
                    !== moment(getValues('appoinmentDate')).format('DD-MM-YYYY HH:mm:ss')
                || newLeadDetail.lead_appointment.method !== getValues('preferredContactMethod')
                || newLeadDetail.lead_appointment.contacted_via !== getValues('practiceContactedVia')
            ) {
                const updateAppointment = {
                    lead_treatment: treatmentId,
                    datetime: `${moment(getValues('appoinmentDate')).format('DD-MM-YYYY HH:mm:ss')}.0Z`,
                    method: getValues('preferredContactMethod'),
                    contacted_via: contactedVia[0],
                    assignees: practiceUserId(getValues('bookedWith')),
                    type: getValues('appointmentType'),
                };
                updateAppointment.lead = tableRowLead.lead_id;
                updateAppointment.id = newLeadDetail.lead_appointment.id;
                dispatch(UpdateAppointment(updateAppointment, practiceId, handleSuccess));
            } else {
                handleNext(true);
            }
        } else {
            const followUpAppointment = {
                lead_treatment: treatmentId,
                datetime: `${moment(getValues('appoinmentDate')).format('DD-MM-YYYY HH:mm:ss')}.0Z`,
                method: getValues('preferredContactMethod'),
                contacted_via: contactedVia[0],
                assignees: practiceUserId(getValues('bookedWith')),
                type: getValues('appointmentType'),
            };
            dispatch(addAppointment(followUpAppointment, practiceId, tableRowLead.lead_id, handleSuccess));
        }
    };

    // ── Imperative API mirrors V1:329-342 ─────────────────────────────────────
    useImperativeHandle(
        ref,
        () => ({
            async submit(status) {
                await handleSubmit(
                    (data) => onSubmit(data, status),
                    (validationErrors) => {
                        console.error('[V2BookingDetailsStep] validation errors:', validationErrors);
                        enqueueSnackbar('Required field is missing', {
                            variant: 'warning',
                            anchorOrigin: { vertical: 'top', horizontal: 'center' },
                        });
                    }
                )();
            },
            isValid() {
                const v = getValues();
                return isBookingDetailsStepValid({
                    treatment: v.treatment,
                    appoinmentDate: v.appoinmentDate,
                    bookedWith: v.bookedWith,
                    assignToState,
                });
            },
        }),
        [assignToState, getValues]
    );

    // ── Treatment edit popover handlers mirror V1:360-378 ────────────────────
    const handleClickAwayLister = () => onCloseEditTreatment();
    const handleSaveTreatment = (data) => {
        setTreatment(data.selectedTreatment);
        setAmount(data.treatmentPrice);
        setValue('treatment', data.selectedTreatment);
        setValue('teethAmount', data.teethAmount);
        localStorageSetItem('localTreatmentData', JSON.stringify({
            treatment: data.selectedTreatment, teethAmount: data.teethAmount, price: data.treatmentPrice,
        }));
        onCloseEditTreatment();
    };
    const handleTreatmentChipClick = (treatment) => {
        const treatmentName = treatment.treatment_item?.name || treatment.name || '';
        setTreatment(treatmentName);
        setAmount(treatment.custom_price || treatment.price || 0);
        setValue('treatment', treatmentName);
        setValue('teethAmount', null);
        localStorageSetItem('localTreatmentData', JSON.stringify({
            treatment: treatmentName, teethAmount: null, price: treatment.custom_price || treatment.price || 0,
        }));
    };
    const handleTreatment = (event) => {
        onEditTreatment();
        setAnchorEditTreatment(event.currentTarget);
    };
    const handleSetAssign = (vals) => {
        setAssignToState(vals);
        setValue('bookedWith', vals);
    };
    const amountHandleChange = (value) => {
        setAmount(value);
        localStorageSetItem('localTreatmentData', JSON.stringify({
            treatment: treatmentData, teethAmount: values.teethAmount, price: value,
        }));
        if (value > 0) {
            setPriceAmountError(false);
            document.body.classList.remove('points-event');
            return;
        }
        setPriceAmountError(true);
    };
    const handleChangeTeeth = (e) => {
        const value = e.target.value > 0 ? e.target.value : '';
        setValue('teethAmount', value);
        localStorageSetItem('localTreatmentData', JSON.stringify({
            treatment: treatmentData, teethAmount: value, price: amount,
        }));
        setTeethAmountError(value === 0);
    };
    const handleChangeDate = ({ newValue, field }) => {
        field.onChange(newValue);
        dispatch(updateSteps('payment'));
    };

    // V1:436-461 — quick-book chips.
    const getQuickBookChips = () => {
        const chips = [];
        const now = moment();
        const popularTimes = suggestions?.popular_times || ['09:00', '14:00', '10:00'];
        popularTimes.forEach((time) => {
            const [hour, minute] = time.split(':');
            const dt = moment().add(1, 'day').hours(parseInt(hour, 10)).minutes(parseInt(minute || 0, 10)).seconds(0);
            chips.push({ label: `Tomorrow ${dt.format('h:mm A')}`, value: dt.toDate() });
        });
        const daysUntilMon = (8 - now.day()) % 7 || 7;
        const [h] = (popularTimes[0] || '10:00').split(':');
        const nextMon = moment().add(daysUntilMon, 'days').hours(parseInt(h, 10)).minutes(0).seconds(0);
        chips.push({ label: `Next Mon ${nextMon.format('h:mm A')}`, value: nextMon.toDate() });
        return chips.slice(0, 4);
    };

    const starredTreatments = treatmentsListV2?.results?.filter((t) => t.is_starred)?.slice(0, 5) || [];

    return (
        <Box data-testid="v2-booking-details-step">
            {/* No inner card chrome and no "Booking details" header — the outer ActionPanel
                already supplies the white panel + border + soft shadow + "Book appointment"
                action header. Adding our own here produced the doubled-card look Raj called
                out on 2026-06-08 ("Booking details inside of a box with a border and drop
                shadow"). Sections below carry the hierarchy via labels + dividers. */}
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ px: 0, py: 0.5 }}>
                    {/* ── TREATMENT ─────────────────────────────────────────── */}
                    <Box sx={SECTION_BLOCK}>
                        <Typography sx={SECTION_LABEL}>Treatment</Typography>
                        <Box sx={V2_INPUT_WRAP_SX}>
                            {EditTreatment && (
                                <TreatmentEditPopover
                                    treatment={treatmentData || ''}
                                    leadTreatmentSpecification={{ teeth_count: values.teethAmount }}
                                    open={EditTreatment}
                                    anchorEl={anchorEditTreatment}
                                    handleClickAwayLister={handleClickAwayLister}
                                    treatmentPrice={amount}
                                    handleSaveTreatment={handleSaveTreatment}
                                    isFlow
                                    disablePortal
                                />
                            )}
                            <TreatmentFlowComponent
                                // This bar IS the Treatment section's panel — same grey
                                // as Team/Appointment below and as the Dentally mode's
                                // sections. Overriding rather than wrapping avoids the
                                // nested double-border look.
                                containerSx={{ ...bookingStepPanelStyle, mb: 0 }}
                                treatmentData={treatmentData}
                                teethAmount={values.teethAmount}
                                handleTreatment={handleTreatment}
                                amount={amount}
                                errorMessage={errors.treatment?.message}
                                amountError={priceAmountError}
                                amountHandleChange={amountHandleChange}
                                handleChangeTeeth={handleChangeTeeth}
                                teethAmountError={teethAmountError}
                                starredTreatments={starredTreatments}
                                onTreatmentChipClick={handleTreatmentChipClick}
                            />
                        </Box>
                    </Box>

                    {/* ── TEAM ──────────────────────────────────────────────── */}
                    <Box sx={SECTION_BLOCK}>
                        <Typography sx={SECTION_LABEL}>Team</Typography>
                        <Box sx={{ ...bookingStepPanelStyle, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <Box>
                                <Typography sx={SUB_LABEL_V2}>
                                    Booked with <span style={{ color: '#FF5630' }}>*</span>
                                </Typography>
                                {bookableStaff.length > 7 && showAllPract && (
                                    <Box sx={{ mb: 0.75 }}>
                                        <input
                                            type="text"
                                            value={practSearch}
                                            onChange={(e) => setPractSearch(e.target.value)}
                                            placeholder="Search practitioners…"
                                            style={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: '10px', padding: '6px 10px', fontSize: '12px', width: '200px', outline: 'none' }}
                                        />
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {(() => {
                                        const q = practSearch.toLowerCase();
                                        const filtered = q ? bookableStaff.filter((u) => u._name.toLowerCase().includes(q)) : bookableStaff;
                                        // Selected first, so collapsing can't hide the person already
                                        // booked with — a plain slice() dropped them off the row.
                                        const wasPicked = (u) => assignToState?.some((a) => a.id === u.id || (typeof a === 'string' && a.includes(u?.user?.first_name)));
                                        const ordered = [...filtered].sort((a, b) => Number(wasPicked(b)) - Number(wasPicked(a)));
                                        const visible = showAllPract ? ordered : ordered.slice(0, MAX_VISIBLE_PRACTITIONERS);
                                        const overflow = ordered.length - MAX_VISIBLE_PRACTITIONERS;
                                        return (
                                            <>
                                                {visible.map((u) => {
                                                    const isSelected = assignToState?.some((a) => a.id === u.id || (typeof a === 'string' && a.includes(u?.user?.first_name)));
                                                    const isPract = hasPractitionerRole(u);
                                                    return (
                                                        <Tooltip
                                                            key={u.id}
                                                            title={u._name}
                                                            arrow
                                                            placement="top"
                                                            componentsProps={{ tooltip: { sx: { bgcolor: '#1d1d1f', fontSize: '11px', fontWeight: 500 } }, arrow: { sx: { color: '#1d1d1f' } } }}
                                                        >
                                                            <ButtonBase
                                                                component="button"
                                                                type="button"
                                                                aria-label={`${isSelected ? 'Deselect' : 'Select'} ${u._name} as booked with`}
                                                                aria-pressed={isSelected}
                                                                onClick={() => {
                                                                    if (isSelected) handleSetAssign(assignToState.filter((a) => a.id !== u.id));
                                                                    else handleSetAssign([...(assignToState || []), u]);
                                                                }}
                                                                sx={{ width: 30, height: 30, borderRadius: '50%', position: 'relative', cursor: 'pointer', border: '2px solid', borderColor: isSelected ? '#00AB55' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isSelected ? 'rgba(0,171,85,0.08)' : (isPract ? 'rgba(7,141,238,0.08)' : 'rgba(133,92,248,0.08)'), fontSize: '11px', fontWeight: 600, color: isSelected ? '#00AB55' : (isPract ? '#078DEE' : '#855CF8'), transition: 'all 150ms ease', '&:hover': { borderColor: isPract ? '#078DEE' : '#855CF8' }, '&:focus-visible': { outline: '2px solid #00AB55', outlineOffset: '2px' } }}
                                                            >
                                                                {u.user?.avatar ? <img src={u.user.avatar} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' }} /> : u._initials}
                                                                {isSelected && (
                                                                    <Box sx={{ position: 'absolute', bottom: -2, right: -2, width: 12, height: 12, borderRadius: '50%', background: '#00AB55', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid #fff' }}>
                                                                        <Iconify icon="eva:checkmark-fill" width={8} sx={{ color: '#fff' }} />
                                                                    </Box>
                                                                )}
                                                            </ButtonBase>
                                                        </Tooltip>
                                                    );
                                                })}
                                                {!showAllPract && overflow > 0 && !q && (
                                                    <Tooltip title={`Show ${overflow} more`} arrow placement="top">
                                                        <ButtonBase
                                                            component="button"
                                                            type="button"
                                                            aria-label={`Show ${overflow} more practitioners`}
                                                            onClick={() => setShowAllPract(true)}
                                                            sx={{ width: 30, height: 30, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px dashed', borderColor: 'rgba(0,0,0,0.16)', color: '#6e6e73', fontSize: '10px', fontWeight: 700, transition: 'all 150ms ease', '&:hover': { borderColor: '#00AB55', color: '#00AB55' }, '&:focus-visible': { outline: '2px solid #00AB55', outlineOffset: '2px' } }}
                                                        >
                                                            +{overflow}
                                                        </ButtonBase>
                                                    </Tooltip>
                                                )}
                                            </>
                                        );
                                    })()}
                                </Box>
                                {errors?.bookedWith && (
                                    <Typography sx={{ fontSize: '11px', color: '#FF5630', mt: 0.5, letterSpacing: '-0.005em' }}>
                                        {errors.bookedWith.message}
                                    </Typography>
                                )}
                            </Box>
                            {assignableUsers.length > 0 && (
                                <Box>
                                    <Typography sx={SUB_LABEL_V2}>Assigned to</Typography>
                                    {assignableUsers.length > MAX_VISIBLE_PRACTITIONERS && showAllAssign && (
                                        <Box sx={{ mb: 0.75 }}>
                                            <input
                                                type="text"
                                                value={assignSearch}
                                                onChange={(e) => setAssignSearch(e.target.value)}
                                                placeholder="Search people…"
                                                aria-label="Search people to assign"
                                                style={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: '10px', padding: '6px 10px', fontSize: '12px', width: '200px', outline: 'none' }}
                                            />
                                        </Box>
                                    )}
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {(() => {
                                        const q = assignSearch.toLowerCase();
                                        const isPicked = (u) => assignToLeadState?.some((a) => a.id === u.id);
                                        const filtered = q
                                            ? assignableUsers.filter((u) => u._name.toLowerCase().includes(q))
                                            : assignableUsers;
                                        // Selected first: collapsing must never hide someone the user
                                        // has already assigned, which a plain slice() would do.
                                        const ordered = [...filtered].sort((a, b) => Number(isPicked(b)) - Number(isPicked(a)));
                                        const visible = showAllAssign ? ordered : ordered.slice(0, MAX_VISIBLE_PRACTITIONERS);
                                        const overflow = ordered.length - MAX_VISIBLE_PRACTITIONERS;
                                        return (
                                          <>
                                        {visible.map((u) => {
                                            const isSelected = isPicked(u);
                                            return (
                                                <Tooltip
                                                    key={u.id}
                                                    title={u._name}
                                                    arrow
                                                    placement="top"
                                                    componentsProps={{ tooltip: { sx: { bgcolor: '#1d1d1f', fontSize: '11px', fontWeight: 500 } }, arrow: { sx: { color: '#1d1d1f' } } }}
                                                >
                                                    <ButtonBase
                                                        component="button"
                                                        type="button"
                                                        aria-label={`${isSelected ? 'Deselect' : 'Select'} ${u._name} as assigned to`}
                                                        aria-pressed={isSelected}
                                                        onClick={() => {
                                                            if (isSelected) setAssignToLeadState(assignToLeadState.filter((a) => a.id !== u.id));
                                                            else setAssignToLeadState([...(assignToLeadState || []), u]);
                                                        }}
                                                        // position:relative (was overflow:hidden) so the
                                                        // selected tick can sit on the edge like it does
                                                        // on "Booked with"; the img is rounded instead.
                                                        sx={{ width: 30, height: 30, borderRadius: '50%', position: 'relative', cursor: 'pointer', border: '2px solid', borderColor: isSelected ? '#00AB55' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isSelected ? 'rgba(0,171,85,0.08)' : 'rgba(133,92,248,0.08)', fontSize: '11px', fontWeight: 600, color: isSelected ? '#00AB55' : '#855CF8', transition: 'all 150ms ease', '&:hover': { borderColor: '#855CF8' }, '&:focus-visible': { outline: '2px solid #00AB55', outlineOffset: '2px' } }}
                                                    >
                                                        {u.user?.avatar ? <img src={u.user.avatar} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' }} /> : u._initials}
                                                        {isSelected && (
                                                            <Box sx={{ position: 'absolute', bottom: -2, right: -2, width: 12, height: 12, borderRadius: '50%', background: '#00AB55', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid #fff' }}>
                                                                <Iconify icon="eva:checkmark-fill" width={8} sx={{ color: '#fff' }} />
                                                            </Box>
                                                        )}
                                                    </ButtonBase>
                                                </Tooltip>
                                            );
                                        })}
                                        {!showAllAssign && overflow > 0 && !q && (
                                            <Tooltip title={`Show ${overflow} more`} arrow placement="top">
                                                <ButtonBase
                                                    component="button"
                                                    type="button"
                                                    aria-label={`Show ${overflow} more people`}
                                                    onClick={() => setShowAllAssign(true)}
                                                    sx={{ width: 30, height: 30, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px dashed', borderColor: 'rgba(0,0,0,0.16)', color: '#6e6e73', fontSize: '10px', fontWeight: 700, transition: 'all 150ms ease', '&:hover': { borderColor: '#00AB55', color: '#00AB55' }, '&:focus-visible': { outline: '2px solid #00AB55', outlineOffset: '2px' } }}
                                                >
                                                    +{overflow}
                                                </ButtonBase>
                                            </Tooltip>
                                        )}
                                          </>
                                        );
                                    })()}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* ── APPOINTMENT ───────────────────────────────────────── */}
                    <Box sx={SECTION_BLOCK}>
                        <Typography sx={SECTION_LABEL}>Appointment</Typography>

                        <Box sx={bookingStepPanelStyle}>
                        {/* Quick pick caption — RescheduleDialog.jsx:140-146 */}
                        <Typography sx={{
                            fontSize: '11px', fontWeight: 600,
                            textTransform: 'uppercase', letterSpacing: '0.06em',
                            color: '#6e6e73', mb: 0.75,
                        }}>
                            Quick pick
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                            {getQuickBookChips().map((chip, idx) => (
                                <Button
                                    key={idx}
                                    size="small"
                                    onClick={() => setValue('appoinmentDate', chip.value)}
                                    sx={quickPickChipSx}
                                    startIcon={<Iconify icon="mdi:clock-outline" width={13} sx={{ color: 'inherit' }} />}
                                >
                                    {chip.label}
                                </Button>
                            ))}
                        </Box>

                        {/* Date picker — V2-styled (borderRadius 12px) */}
                        <Box sx={{ mb: 2, ...V2_INPUT_WRAP_SX }}>
                            <Controller
                                name="appoinmentDate"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <DateTimePicker
                                        {...field}
                                        label="Appointment date (Required)"
                                        value={field.value || null}
                                        format="dd/MM/yyyy HH:mm"
                                        onChange={(newValue) => handleChangeDate({ newValue, field })}
                                        ampm={false}
                                        slotProps={{ textField: { fullWidth: true, size: 'small', error: !!error, helperText: error?.message, onKeyDown: (e) => e.preventDefault() } }}
                                    />
                                )}
                            />
                        </Box>

                        {/* Method / Type / Contact — V2 chip groups */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, columnGap: 2, rowGap: 2 }}>
                            <Controller
                                name="preferredContactMethod"
                                control={control}
                                render={({ field }) => (
                                    <V2ChoiceGroup label="Method" value={field.value} onChange={field.onChange} options={appointmentMethod || []} />
                                )}
                            />
                            <Controller
                                name="appointmentType"
                                control={control}
                                render={({ field }) => (
                                    <V2ChoiceGroup label="Type" value={field.value} onChange={field.onChange} options={appointmentType || []} />
                                )}
                            />
                            <Controller
                                name="practiceContactedVia"
                                control={control}
                                render={({ field }) => (
                                    <V2ChoiceGroup
                                        label="Contact"
                                        value={field.value}
                                        onChange={field.onChange}
                                        options={[
                                            { value: 'EMAIL', label: 'Email' },
                                            { value: 'PHONE', label: 'Phone' },
                                            { value: 'WHATSAPP', label: 'WhatsApp' },
                                        ]}
                                    />
                                )}
                            />
                        </Box>
                        </Box>
                    </Box>

                    {/* ── SUGGESTIONS ───────────────────────────────────────── */}
                    {(suggestions?.suggested_practitioner || suggestions?.frequently_used_assignees?.length > 0) && (
                        <Box sx={SECTION_BLOCK_LAST}>
                            <Typography sx={{
                                fontSize: '11px', fontWeight: 600,
                                textTransform: 'uppercase', letterSpacing: '0.06em',
                                color: '#6e6e73', mb: 0.75,
                            }}>
                                Suggested
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {suggestions?.suggested_practitioner && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            const matchedPU = practiceUser?.results?.find((pu) => pu.id === suggestions.suggested_practitioner.id);
                                            if (matchedPU) handleSetAssign([matchedPU]);
                                        }}
                                        startIcon={<Iconify icon="mdi:account-check-outline" width={16} height={16} />}
                                        sx={v2PillBtnSx(false)}
                                    >
                                        {`${suggestions.suggested_practitioner.first_name} ${suggestions.suggested_practitioner.last_name} (${suggestions.suggested_practitioner.booking_count} bookings)`}
                                    </Button>
                                )}
                                {suggestions?.frequently_used_assignees?.slice(0, 3).map((a) => {
                                    const matchedPU = practiceUser?.results?.find((pu) => pu.id === a.id);
                                    const isSelected = assignToLeadState?.some((s) => s?.id === a.id);
                                    return (
                                        <Button
                                            key={a.id}
                                            variant="outlined"
                                            size="small"
                                            onClick={() => matchedPU && setAssignToLeadState([matchedPU])}
                                            startIcon={(
                                                <Iconify
                                                    icon={isSelected ? 'bi:check-circle-fill' : 'mdi:account-outline'}
                                                    width={16}
                                                    height={16}
                                                />
                                            )}
                                            sx={v2PillBtnSx(isSelected)}
                                        >
                                            {`${a.first_name} ${a.last_name}`.trim()}
                                        </Button>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </Box>
            </FormProvider>
        </Box>
    );
});

V2BookingDetailsStep.propTypes = {
    /** Advances the wizard (called with true after successful submit, mirrors V1 handleNext). */
    handleNext: PropTypes.func.isRequired,
    /**
     * 2026-06-07 bundle: push-based validity reporter. Called whenever the user changes
     * one of the required-field inputs (treatment / appoinmentDate / bookedWith).
     * Parent (FollowUpV2) uses it to disable the shell's Next button until valid.
     * Mirrors V1 OverView.jsx:407 setPmsConfirmStepState pattern.
     */
    onValidityChange: PropTypes.func,
};

V2BookingDetailsStep.defaultProps = {
    onValidityChange: undefined,
};

export default V2BookingDetailsStep;
